/* eslint-disable @typescript-eslint/no-use-before-define */
import { People } from '@mui/icons-material';
import { Alert, Box, Stack, Tooltip } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { Label } from 'mdi-material-ui';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { UseFormReturn } from 'react-hook-form/dist/types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    activityAttachmentsApi,
    addActivityComment,
    addActivityToTheWatchlist,
    createActivity,
    getActivityContext,
    getActivityWatchers,
    removeActivityFromTheWatchlist,
    submitActivity,
    updateActivity,
} from '../../api/activities-nim';
import { ADAMS_ACCESS_PATH, getAdamsAccessData } from '../../api/adams';
import { DOSE_EXCEED_PATH, getDoseExceedInfo } from '../../api/dose-exceed';
import {
    ActivityContextReadDTO,
    ActivityNimReadDTO,
    ActivityParticipantNimReadDTO,
    ActivityStatusCode,
    ActivityTypeCode,
    AttachmentDTO,
    BinaryChoiceCode,
    DocumentActionDTO,
    DocumentType,
    DurationTypeCode,
    FacilityCode,
    FeatureFlag,
    ListOfValuesReadDTO,
    LocationDTO,
    ParticipantRoleCode,
    PersonSmallDTO,
    RadiographyDeviceCode,
    SystemBsSrcReadDTO,
    TaskType,
    WorkingTimeCode,
} from '../../api/dto';
import { getActivityTags } from '../../api/tags';
import { useApplicationContext } from '../../components/application/ApplicationContextProvider';
import { CommentList } from '../../components/comments/CommentList';
import { COMMON_DOCUMENT_BUTTONS, DocumentButtonsStack } from '../../components/documents/DocumentButtonsStack';
import { DocumentPageStructure } from '../../components/documents/DocumentPageStructure';
import { LinkedDocumentsPanel } from '../../components/documents/LinkedDocumentsPanel';
import { ResponsiveButton } from '../../components/ResponsiveButton';
import { useTaskContext } from '../../components/TaskContext';
import { TaskActions } from '../../components/tasks/TaskActions';
import { TaskHistory } from '../../components/tasks/TaskHistory';
import { useTasks } from '../../components/tasks/useTasks';
import { CustomErrorResolver, ValidationErrorsPanel } from '../../components/ValidationErrorsPanel';
import { WorkflowHistory } from '../../components/WorkflowHistory';
import { useUnsavedChangesPrompt } from '../../hooks/useUnsavedChangesPrompt';
import { useDocumentSaveErrorCallback } from '../../hooks/validation';
import { artificialDelay } from '../../lib/delay';
import { EntityType } from '../../lib/information/types/EntityType';
import { translateValue } from '../../lib/language';
import { mapActivityToUpdateDTO } from '../../lib/records/activity';
import {
    facilitiesWithAnyWorkingTime,
    facilitiesWithExtWorkingTime,
    facilitiesWithOtherWorkingTime,
    getAccessEndByScheduledEnd,
    getAccessStartByScheduledStart,
    makeDefaultActivityValues,
    makeDefaultValuesFromClonedActivity,
} from '../../lib/records/documents/activity';
import { areLocationsPartOfFacility } from '../../lib/records/location';
import { ActivityContentHeader } from '../../pages/activities/ActivityContentHeader';
import { ActivityPageData } from '../../pages/activities/ActivityPageData';
import { ActivityPageVariant } from '../../pages/activities/ActivityPageVariant';
import { PrintGuidedVisitsButton } from '../../pages/activities/PrintGuidedVisitsButton';
import { AttachmentsAction } from '../../pages/documents/ndc/AttachmentsAction';
import { getOIMUrl } from '../../routes/oimRoutes';
import { getRoute, ROUTES } from '../../routes/routes';
import { FieldsAttributesContextProvider } from '../FieldsAttributesContext';
import { FormGenerator } from '../FormGenerator';
import { PreciseRulesDTO } from '../rule/deserializer';
import { useFormRules } from '../rule/useFormRules';
import { useIsSavableFromRules } from '../rule/useIsSavableFromRules';
import { activityFormSchema } from '../schemas/documents/activity';
import { activityChangeRequestFormSchemaFromActivity } from '../schemas/documents/activityChangeRequest';
import { getReadActivityData, isCreateVariant, splitFieldPath } from '../utils';
import { TaglistDialog } from './activityHelpers/TaglistDialog';
import { WatchlistDialog } from './activityHelpers/WatchlistDialog';

const ESP_FACILITY_CODES = [
    // FacilityCode.PS,
    // FacilityCode.BOOSTER,
    // FacilityCode.LINAC2,
    // FacilityCode.LINAC3,
    // FacilityCode.LINAC4,
    // FacilityCode.SPS,
    FacilityCode.LHC,
];

interface ActivityFormProps {
    data: ActivityPageData;
    refreshData: () => void;
    rules: PreciseRulesDTO<ActivityContextReadDTO>;
    activityTypes: ListOfValuesReadDTO[];
    facilities: ListOfValuesReadDTO[];
    testQuestionTypes: ListOfValuesReadDTO[];
    workingTimes: ListOfValuesReadDTO[];
    durationTypes: ListOfValuesReadDTO[];
    participantRoles: ListOfValuesReadDTO[];
    isUrgentIntervention: boolean;
}

export const ActivityForm: React.FC<ActivityFormProps> = ({
    data: pageData,
    refreshData,
    rules,
    activityTypes,
    facilities: allFacilities,
    testQuestionTypes,
    workingTimes,
    durationTypes,
    participantRoles,
    isUrgentIntervention,
}) => {
    const { withDispatchTaskEvent } = useTaskContext();
    const { currentUser, features, environment } = useApplicationContext();
    const activityId = isCreateVariant(pageData.variant) ? undefined : getReadActivityData(pageData).activityData.id;
    const intl = useIntl();
    const { variant } = pageData;
    const defaultValues = useMemo(
        () =>
            pageData.variant === ActivityPageVariant.READ ||
            pageData.variant === ActivityPageVariant.READ_CHANGE_REQUEST
                ? pageData.activityData
                : pageData.variant === ActivityPageVariant.CLONE
                ? makeDefaultValuesFromClonedActivity(pageData.activityData, pageData.activityData.isTemplate ?? false)
                : pageData.variant === ActivityPageVariant.CLONE_FROM_TEMPLATE
                ? makeDefaultValuesFromClonedActivity(pageData.activityData, false)
                : makeDefaultActivityValues(
                      activityTypes,
                      testQuestionTypes,
                      workingTimes,
                      durationTypes,
                      isUrgentIntervention,
                      currentUser
                  ),
        [pageData]
    );
    const tasks = useTasks(
        pageData.variant === ActivityPageVariant.READ || pageData.variant === ActivityPageVariant.READ_CHANGE_REQUEST
            ? {
                  documentDetails: [
                      {
                          documentType: {
                              [ActivityPageVariant.READ]: DocumentType.ACTIVITY,
                              [ActivityPageVariant.READ_CHANGE_REQUEST]: DocumentType.ACTIVITY_CR,
                          }[pageData.variant],
                          documentId: pageData.activityData.id,
                      },
                  ],
                  onTaskSubmit: (taskHandler) => {
                      clearErrors();
                      return handleSubmit((data) => taskHandler(data))();
                  },
                  onTaskError: (response: AxiosResponse) => {
                      refetchContext();
                      handleFormSaveError(response, { validate: true, submit: false });
                  },
                  refreshData,
              }
            : undefined
    );
    const formRef = useRef<UseFormReturn<ActivityNimReadDTO>>();

    const isContextFetchingEnabled = tasks.currentTask !== null;
    const { data: contextData, refetch: refetchContext } = useQuery(
        [
            'activity',
            'context',
            pageData.variant === ActivityPageVariant.READ ? pageData.activityData.id : 0,
            tasks.currentTask?.id,
        ],
        () =>
            getActivityContext({
                activity: formRef.current!.getValues() as any,
                taskId: tasks.currentTask?.id ?? '',
                documentAction:
                    variant === ActivityPageVariant.CLONE_FROM_TEMPLATE ? DocumentActionDTO.CLONE_FROM_TEMPLATE : null,
            }),
        {
            enabled: isContextFetchingEnabled,
        }
    );
    const formRefWatch:
        | ((name: keyof Pick<ActivityNimReadDTO, 'type' | 'facility'>) => ListOfValuesReadDTO | undefined)
        | undefined = formRef.current?.watch;
    const typeCode = formRefWatch?.('type')?.code ?? null;
    const facilityCode = formRefWatch?.('facility')?.code ?? null;
    const context: ActivityContextReadDTO | null = useMemo(
        () =>
            rules.context !== null
                ? {
                      ...rules.context,
                      ...contextData?.context,
                      documentAction:
                          variant === ActivityPageVariant.CREATE
                              ? DocumentActionDTO.CREATE
                              : variant === ActivityPageVariant.CLONE_FROM_TEMPLATE
                              ? DocumentActionDTO.CLONE_FROM_TEMPLATE
                              : null,
                      task: tasks.currentTask?.type?.type ?? null,
                      taskCode: tasks.currentTask?.code ?? null,
                      isChangeRequest: variant === ActivityPageVariant.READ_CHANGE_REQUEST,
                      typeCode,
                      facilityCode,
                  }
                : null,
        [rules.context, contextData?.context, tasks.currentTask, typeCode, facilityCode]
    );
    const { form, attributes } = useFormRules<ActivityNimReadDTO>({
        formProps: {
            mode: 'onSubmit',
            defaultValues,
        },
        rules,
        originalValues: defaultValues,
        context: context as any,
    });
    useLayoutEffect(() => {
        formRef.current = form;
    }, [form, formRef]);
    const { handleSubmit, clearErrors, watch, formState } = form;
    const depsContextRefresh = watch(['facility', 'type', 'activityLocations', 'activityResponsible']);
    // Similar to what was done on the IS37
    const isCompleteOnly = tasks.currentTask && [TaskType.CREATE_CHANGE_REQUEST].includes(tasks.currentTask.type.type);
    const isSavable =
        useIsSavableFromRules(attributes) && !isCompleteOnly && variant !== ActivityPageVariant.READ_CHANGE_REQUEST;
    const isCompletable = tasks.isLoading || !!tasks.assignableTasks.length;
    const { isDirty: formIsDirty } = formState;
    const isDirty =
        formIsDirty || (variant !== ActivityPageVariant.READ && variant !== ActivityPageVariant.READ_CHANGE_REQUEST);

    const navigate = useNavigate();

    const handleFormSaveError = useDocumentSaveErrorCallback(form, DocumentType.ACTIVITY);
    const activityNotActiveStatusCodes: string[] = [
        ActivityStatusCode.CLOSED,
        ActivityStatusCode.REJECTED,
        ActivityStatusCode.CANCELLED,
    ] satisfies ActivityStatusCode[];
    const proposedDate = form.watch('proposedDate');
    const duration = form.watch('duration');
    const durationType = form.watch('durationType');
    const [proposedEndDate, setProposedEndDate] = useState<Date | null>(null);

    useEffect(() => {
        if (
            (context?.currentStatusCode && activityNotActiveStatusCodes.includes(context.currentStatusCode)) ||
            !(proposedDate && duration && durationType)
        ) {
            setProposedEndDate(null);
            return;
        }

        const parsedDuration = Number(duration) || 0;
        const endDate = new Date(proposedDate);

        if (isNaN(endDate.getTime())) {
            setProposedEndDate(null);
            return;
        }

        if (parsedDuration > 0) {
            if (durationType.code === DurationTypeCode.DAY) {
                endDate.setDate(endDate.getDate() + (parsedDuration - 1));
            } else if (durationType.code === DurationTypeCode.HOUR) {
                endDate.setHours(endDate.getHours() + (parsedDuration - 1));
            } else {
                endDate.setMinutes(endDate.getMinutes() + (parsedDuration - 1));
            }
        }
        setProposedEndDate(endDate);
    }, [proposedDate, duration, durationType, context?.currentStatusCode]);

    const copyProposedDate = useCallback(() => {
        const {
            earliestStart,
            latestEnd,
            proposedInterventionPeriod,
            workingTimeAvail,
            workingTimeAvailTxt,
            workingTimeAvailStart,
            workingTimeAvailEnd,
        } = form.getValues();

        const setDateValues = (start: Date, end: Date) => {
            const startISO = start.toISOString();
            const endISO = end.toISOString();

            form.setValue('scheduledStart', startISO, { shouldDirty: true });
            form.setValue('accessStart', startISO, { shouldDirty: true });
            form.setValue('scheduledEnd', endISO, { shouldDirty: true });
            form.setValue('accessEnd', endISO, { shouldDirty: true });
        };

        if (proposedInterventionPeriod) {
            form.setValue('interventionPeriod', proposedInterventionPeriod, { shouldDirty: true });
        }

        if (proposedDate && duration && durationType) {
            const parsedDuration = typeof duration === 'string' ? parseInt(duration, 10) : duration;
            const startDate = new Date(proposedDate);
            let endDate = new Date(startDate);

            if (!isNaN(endDate.getTime()) && !isNaN(parsedDuration)) {
                if (durationType.code === DurationTypeCode.DAY && parsedDuration > 0) {
                    endDate.setDate(startDate.getDate() + (parsedDuration - 1));
                }
                setDateValues(startDate, endDate);
            }
        } else if (earliestStart && latestEnd) {
            const startDate = new Date(earliestStart);
            const endDate = new Date(latestEnd);
            if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
                setDateValues(startDate, endDate);
            }
        }

        if (workingTimeAvail) {
            form.setValue('workingTimeGtd', workingTimeAvail, { shouldDirty: true });
            form.trigger('workingTimeGtd');
            form.setValue('workingTimeGrtdTxt', workingTimeAvailTxt, { shouldDirty: true });

            if (workingTimeAvail.code === WorkingTimeCode.OTHER) {
                form.setValue('workingTimeGrtdStart', workingTimeAvailStart, { shouldDirty: true });
                form.setValue('workingTimeGrtdEnd', workingTimeAvailEnd, { shouldDirty: true });
            }
        }
    }, [form.getValues, form.setValue, proposedDate, duration, durationType]);

    const locations =
        form
            .getValues()
            .activityLocations?.map((activityLocation) => activityLocation?.location)
            .filter((location) => !!location) ?? [];

    const accessPoints =
        form
            .getValues()
            .activityLocations?.flatMap((activityLocation) => activityLocation?.accessPoints)
            .filter((location) => !!location) ?? [];
    const accessPointNames = [...new Set(accessPoints.map((ap) => ap.name))];

    const participants = form.getValues().activityParticipants;
    const egroupIds = participants?.flatMap((p) => p.egroup?.id).filter((id) => id);
    const personIds = participants?.flatMap((p) => p.person?.id).filter((id) => id !== undefined);

    const isFromEspTemplate =
        pageData.variant === ActivityPageVariant.READ &&
        pageData.activityData?.activityParent?.id != null &&
        form.getValues().espQuestions?.requestForElectricalRiskAssessment?.code === BinaryChoiceCode.YES;

    const { data: adamsData } = useQuery(
        [ADAMS_ACCESS_PATH, accessPointNames, egroupIds, personIds],
        () => {
            if (accessPointNames.length > 0) {
                return getAdamsAccessData({ accessPointNames, egroupIds, personIds });
            } else {
                return Promise.resolve([]);
            }
        },
        {
            enabled: accessPointNames.length > 0,
        }
    );

    const { data: doseExceedInfo } = useQuery(
        [DOSE_EXCEED_PATH, activityId],
        () => getDoseExceedInfo({ activityId: activityId! }),
        {
            enabled: !!activityId,
        }
    );

    const { data: activityTags, refetch: refetchActivityTags } = useQuery(
        ['tags', activityId],
        () => getActivityTags({ activityId: activityId! }),
        {
            enabled: !!activityId && !!features[FeatureFlag.V9],
        }
    );

    const showProposalPanel =
        form.getValues().facility?.code !== FacilityCode.SURFACE &&
        form.getValues().type?.code !== ActivityTypeCode.URGENT_INTERVENTION;
    const showCopyProposedDateButton =
        isSavable && showProposalPanel && !!attributes.scheduledStart?.editable && attributes.scheduledEnd?.editable;

    const activityCodesMasks: string[] = [
        ActivityTypeCode.PROFESSIONAL_VISIT,
        ActivityTypeCode.URGENT_INTERVENTION,
        ActivityTypeCode.GUIDED_VISIT,
    ] satisfies ActivityTypeCode[];

    const typeValue = form.watch('type');
    const facilityValue = form.watch('facility');
    const scheduledStartValue = form.watch('scheduledStart');
    const scheduledEndValue = form.watch('scheduledEnd');
    const interventionPeriodValue = form.watch('interventionPeriod');
    const mask = useMemo(() => {
        if (typeValue && activityCodesMasks.includes(typeValue.code)) {
            return translateValue(typeValue, intl.locale).label;
        } else if (facilityValue) {
            return translateValue(facilityValue, intl.locale).label;
        }
        return undefined;
    }, [typeValue, facilityValue]);

    const { interventionPeriod, proposedInterventionPeriod, scheduledStart, scheduledEnd } = form.getValues();

    const proposedDatesNotInIntPeriod = useMemo(() => {
        if (proposedInterventionPeriod && proposedDate && proposedEndDate) {
            return (
                Date.parse(proposedInterventionPeriod.periodStart) > Date.parse(proposedDate) ||
                Date.parse(proposedInterventionPeriod.periodEnd) < proposedEndDate.getTime()
            );
        }
        return false;
    }, [proposedInterventionPeriod, proposedDate, proposedEndDate]);

    const scheduledDatesNotInIntPeriod = useMemo(() => {
        if (interventionPeriod && scheduledStart && scheduledEnd) {
            return (
                Date.parse(interventionPeriod.periodStart) > Date.parse(scheduledStart) ||
                Date.parse(interventionPeriod.periodEnd) < Date.parse(scheduledEnd)
            );
        }
        return false;
    }, [interventionPeriod, scheduledStart, scheduledEnd]);

    const isParticipantListEmpty =
        !!context?.currentStatusCode &&
        activityNotActiveStatusCodes.includes(context.currentStatusCode) &&
        form.getValues().activityParticipants.length === 0;

    const isSystemPartOfFacility = (facility: ListOfValuesReadDTO | null, system: SystemBsSrcReadDTO | null) =>
        facility && system ? system?.facility?.code === facility?.code : true;

    const isEspFormEnabled = (facility: ListOfValuesReadDTO | null) =>
        facility && ESP_FACILITY_CODES.includes(facility.code as FacilityCode);

    const attachments: AttachmentDTO[] = form.watch('attachments') ?? [];

    const isTaskChangeRequest = tasks.currentTask?.type?.type === TaskType.CREATE_CHANGE_REQUEST;

    const uiSchemaActivity = useMemo(() => {
        const schema = activityFormSchema(
            intl,
            pageData.variant === ActivityPageVariant.READ
                ? pageData.activityData.changeRequestActivity?.id ?? pageData.activityData.id
                : null,
            [
                ActivityPageVariant.CREATE,
                ActivityPageVariant.CLONE,
                ActivityPageVariant.CLONE_FROM_TEMPLATE,
                ActivityPageVariant.CREATE_TEMPLATE,
            ].includes(pageData.variant),
            copyProposedDate,
            showCopyProposedDateButton,
            proposedEndDate,
            locations,
            accessPointNames,
            form.getValues().type?.code === ActivityTypeCode.URGENT_INTERVENTION,
            isTaskChangeRequest,
            pageData.variant === ActivityPageVariant.READ
                ? pageData.activityData.status?.code ?? ActivityStatusCode.SAVED
                : ActivityStatusCode.SAVED,
            adamsData,
            pageData.variant === ActivityPageVariant.CLONE_FROM_TEMPLATE ? [] : attachments,
            doseExceedInfo,
            areLocationsPartOfFacility({
                facility: form.getValues().facility,
                locations: locations,
            }),
            isSystemPartOfFacility(form.getValues().facility, form.getValues().system),
            !attributes.testQuestions?.editable,
            proposedDatesNotInIntPeriod,
            scheduledDatesNotInIntPeriod,
            isParticipantListEmpty,
            showProposalPanel,
            context?.isRpAssessmentLinked ?? false,
            isEspFormEnabled(form.getValues().facility)!!,
            isFromEspTemplate,
            pageData.variant === ActivityPageVariant.READ ||
                pageData.variant === ActivityPageVariant.READ_CHANGE_REQUEST
                ? pageData.activityData
                : null,
            pageData.variant === ActivityPageVariant.READ &&
                !!pageData.activityData.status &&
                !activityNotActiveStatusCodes.includes(pageData.activityData.status.code) &&
                typeValue?.code !== ActivityTypeCode.URGENT_INTERVENTION,
            refreshData,
            pageData.variant === ActivityPageVariant.READ ? pageData.activityData.notRequiresSafetyApproval!! : false,
            features
        );
        if (pageData.variant === ActivityPageVariant.READ_CHANGE_REQUEST) {
            return activityChangeRequestFormSchemaFromActivity(
                schema,
                pageData.activityData.activitySnapshot
                    ? pageData.activityData.activitySnapshot
                    : pageData.parentActivityData,
                pageData.activityData
            );
        } else {
            return schema;
        }
    }, [intl, pageData, copyProposedDate, locations, context?.isRpAssessmentLinked, tasks.assignableTasks]);

    // Custom error handler for the preassessment confirmation field
    const preassessmentConfirmationPath = useMemo(() => splitFieldPath('espQuestions.preassessmentConfirmation'), []);
    const customErrorResolver: CustomErrorResolver = useCallback(
        (error) => {
            const isPreassessmentConfirmationError = error.path.length === 1 && error.path[0] === 'espQuestions';
            if (!isPreassessmentConfirmationError) {
                return error;
            }
            return {
                ...error,
                path: preassessmentConfirmationPath,
                labels: [
                    intl.formatMessage({ id: 'activity.panel.espPreassessment' }),
                    intl.formatMessage({ id: 'activity.field.preassessmentConfirmation.title' }),
                ],
            };
        },
        [intl, preassessmentConfirmationPath]
    );

    useLayoutEffect(() => void refetchContext(), [JSON.stringify(depsContextRefresh)]);

    const { mutateAsync: saveActivity, isLoading: isSaveLoading } = useMutation(
        ({ data: formData, submit = false }: { data: any; submit?: boolean }) => {
            if (variant === ActivityPageVariant.READ_CHANGE_REQUEST) {
                throw new Error();
            }
            if (variant === ActivityPageVariant.READ) {
                const updateOrSubmit = submit ? submitActivity : updateActivity;
                if (tasks.currentTask === null) {
                    throw new Error();
                }
                return updateOrSubmit({
                    id: pageData.activityData.id,
                    taskId: tasks.currentTask.id,
                    data: mapActivityToUpdateDTO(formData),
                });
            } else {
                if (submit) {
                    throw new Error(); // Must be created first
                } else if (variant === ActivityPageVariant.CREATE_TEMPLATE) {
                    formData.isTemplate = true;
                }
                if (variant === ActivityPageVariant.CREATE_TEMPLATE) {
                    formData.isTemplate = true;
                }
                if (variant === ActivityPageVariant.CLONE_FROM_TEMPLATE) {
                    formData.activityParent = { id: pageData.activityData.id };
                }
                return createActivity({ data: mapActivityToUpdateDTO(formData) }).then(artificialDelay);
            }
        },
        {
            onSuccess: (result) => {
                if (variant === ActivityPageVariant.READ) {
                    refreshData();
                } else {
                    navigate(
                        getRoute({
                            path: ROUTES.activity.view,
                            params: { id: String(result.id) },
                        }),
                        {
                            replace: true,
                        }
                    );
                }
            },
            onError: (response: AxiosResponse) => {
                refetchContext();
                handleFormSaveError(response);
            },
        }
    );

    const isLoading = isSaveLoading || tasks.disabled;

    const withUnsavedChangesPrompt = useUnsavedChangesPrompt({
        when: !isLoading && formIsDirty,
    });
    const refreshDataWithUnsavedChangesPrompt = () => withUnsavedChangesPrompt(refreshData);

    const responsible = form.watch('activityResponsible');
    useEffect(() => {
        // This is very close to OIM, except that if the user deletes the supervisor then it won't be re-added automatically
        const supervisorType = participantRoles.find(({ code }) => code === ParticipantRoleCode.SUPERVISOR);
        const { activityParticipants, contactPhone } = form.getValues();
        if (
            responsible &&
            !contactPhone &&
            attributes.contactPhone?.visible &&
            attributes.contactPhone?.editable &&
            !responsible.phone1?.includes('X')
        ) {
            form.setValue('contactPhone', responsible.phone1);
        }
        if (
            responsible &&
            supervisorType &&
            !activityParticipants?.some((participant) => participant.role?.code === supervisorType.code)
        ) {
            // The responsible is not declared as a participant, adding it automatically
            form.setValue('activityParticipants', [
                {
                    role: supervisorType,
                    person: responsible as PersonSmallDTO,
                } satisfies Partial<ActivityParticipantNimReadDTO> as ActivityParticipantNimReadDTO,
                ...(activityParticipants ?? []),
            ] satisfies ActivityParticipantNimReadDTO[] as any); // Unfortunately without this cast, typescript fails TS2589 (infinitely deep type)
            form.trigger('activityParticipants.0'); // https://gitlab.cern.ch/eis/cap/impact/impact-ng/-/merge_requests/1661#note_9338350
        }
    }, [responsible, form.getValues, form.setValue, form.trigger as any]); // Same here

    const surfaceFacility = allFacilities.filter(({ code }) => code === FacilityCode.SURFACE)[0];
    const activityLocationCommonFacility = useMemo(() => {
        const facilities = Object.entries(
            Object.fromEntries(
                locations.map(({ facility }) => facility ?? surfaceFacility).map((value) => [value.code, value])
            )
        );
        return facilities.length === 1 ? facilities[0][1] : null;
    }, [locations]);

    useEffect(() => {
        const { facility } = form.getValues();
        if (activityLocationCommonFacility && facility == null) {
            form.setValue('facility', activityLocationCommonFacility);
        }
    }, [activityLocationCommonFacility, form.getValues, form.setValue]);

    useLayoutEffect(() => {
        if (pageData.variant === ActivityPageVariant.CLONE || ActivityPageVariant.CLONE_FROM_TEMPLATE) return;
        const { activityResponsible, description, workingTimeAvail, workingTimeGtd } = form.getValues();
        const otherWorkingTime = workingTimes.find(({ code }) => code === WorkingTimeCode.OTHER)!;
        if (facilityValue && facilitiesWithExtWorkingTime.includes(facilityValue.code)) {
            const extendedWorkingTime = workingTimes.find(({ code }) => code === WorkingTimeCode.EXTENDED_NORMAL)!;
            if (attributes.workingTimeAvail?.editable) {
                form.setValue('workingTimeAvail', extendedWorkingTime);
            }
            if (attributes.workingTimeGtd?.editable) {
                form.setValue('workingTimeGtd', extendedWorkingTime);
            }
        }
        if (facilityValue && facilitiesWithAnyWorkingTime.includes(facilityValue.code)) {
            const anytimeWorkingTime = workingTimes.find(({ code }) => code === WorkingTimeCode.ANY_TIME)!;
            if (attributes.workingTimeAvail?.editable) {
                form.setValue('workingTimeAvail', anytimeWorkingTime);
            }
            if (attributes.workingTimeGtd?.editable) {
                form.setValue('workingTimeGtd', anytimeWorkingTime);
            }
        }
        if (facilityValue && facilityValue.code === FacilityCode.ATELIER_109) {
            if (!description && attributes.description?.editable) {
                const defaultDesc: string = 'TREC number(s):';
                form.setValue('description', defaultDesc);
            }
        }
        if (facilityValue && facilitiesWithOtherWorkingTime.includes(facilityValue.code)) {
            if (attributes.workingTimeAvail?.editable) {
                form.setValue('workingTimeAvail', otherWorkingTime);
                form.trigger('workingTimeAvail');
            }
            if (attributes.workingTimeGtd?.editable) {
                form.setValue('workingTimeGtd', otherWorkingTime);
                form.trigger('workingTimeGtd');
            }
        }
        if (facilityValue && facilityValue.code === FacilityCode.ATLAS) {
            const ATLAS_WORK_DAY_START_HOUR = 7;
            const ATLAS_WORK_DAY_START_MINUTE = 30;
            const ATLAS_WORK_DAY_END_HOUR = 19;
            const ATLAS_WORK_DAY_END_MINUTE = 30;
            const startDateTime = new Date();
            startDateTime.setHours(ATLAS_WORK_DAY_START_HOUR, ATLAS_WORK_DAY_START_MINUTE);
            const endDateTime = new Date();
            endDateTime.setHours(ATLAS_WORK_DAY_END_HOUR, ATLAS_WORK_DAY_END_MINUTE);
            if (workingTimeAvail?.code === otherWorkingTime.code && attributes.workingTimeAvail?.editable) {
                form.setValue('workingTimeAvailStart', startDateTime.toISOString());
                form.setValue('workingTimeAvailEnd', endDateTime.toISOString());
            }
            if (workingTimeGtd?.code === otherWorkingTime.code && attributes.workingTimeGtd?.editable) {
                form.setValue('workingTimeGrtdStart', startDateTime.toISOString());
                form.setValue('workingTimeGrtdEnd', endDateTime.toISOString());
            }
        }
        if (typeValue && typeValue.code === ActivityTypeCode.URGENT_INTERVENTION) {
            if (!activityResponsible && attributes.activityResponsible?.editable)
                form.setValue('activityResponsible', currentUser);
        }
    }, [facilityValue, typeValue, form.setValue, form.trigger as any]);

    useLayoutEffect(() => {
        if (scheduledStartValue && attributes.accessStart?.editable && attributes.accessEnd?.visible) {
            form.setValue('accessStart', getAccessStartByScheduledStart(scheduledStartValue, facilityValue?.code));
        }
    }, [scheduledStartValue]);

    useLayoutEffect(() => {
        if (scheduledEndValue && attributes.accessEnd?.editable && attributes.accessEnd?.visible) {
            form.setValue(
                'accessEnd',
                getAccessEndByScheduledEnd(scheduledEndValue, facilityValue?.code, interventionPeriodValue?.periodEnd)
            );
        }
    }, [scheduledEndValue]);

    useLayoutEffect(() => {
        if (attributes.system && !attributes.system?.visible) {
            form.setValue('system', null);
        }
    }, [facilityValue]);

    // Only useful if the access points are not editable
    useEffect(() => {
        const { unsubscribe } = watch((value, { name }) => {
            if (!!name) {
                const path = splitFieldPath(name as any);
                if (path.length === 3 && path[0] === 'activityLocations' && path[2] === 'location') {
                    const location = form.getValues(name as any) as LocationDTO;
                    const accessPointsPath = `activityLocations.${path[1]}.accessPoints`;
                    form.setValue(accessPointsPath as any, location?.accessPoints);
                    form.trigger(accessPointsPath as any);
                }
            }
        });
        return () => unsubscribe();
    }, [watch, form.getValues, form.setValue, form.trigger as any]);

    const isOIMActivity =
        !isCreateVariant(pageData.variant) && getReadActivityData(pageData).activityData.source === 'OIM';

    const { data: watchersData, refetch: refetchWatchersData } = useQuery(
        [
            'activity',
            'watchlist',
            pageData.variant === ActivityPageVariant.READ ? pageData.activityData.id : 0,
            activityId,
        ],
        () => getActivityWatchers({ id: activityId! }),
        {
            enabled: !!activityId,
        }
    );
    const [watchlistOpen, setWatchlistOpen] = useState(false);
    const handleWatchlistOpen = () => setWatchlistOpen(true);
    const handleWatchlistClose = () => setWatchlistOpen(false);
    const [isWatching, setIsWatching] = useState(context?.isOnTheWatchlist);

    const { mutate: addToWatchlist, isLoading: isAddingToWatchlist } = useMutation(
        async () => {
            if (activityId) {
                await addActivityToTheWatchlist({ id: activityId });
                await refetchWatchersData();
            }
        },
        {
            onSuccess: () => {
                setIsWatching(true);
            },
            onError: () => toast.error(intl.formatMessage({ id: 'document.watchlist.error' })),
        }
    );

    const { mutate: removeFromWatchlist, isLoading: isRemovingFromWatchlist } = useMutation(
        async () => {
            if (activityId) {
                await removeActivityFromTheWatchlist({ id: activityId });
                await refetchWatchersData();
            }
        },
        {
            onSuccess: () => {
                setIsWatching(false);
            },
            onError: () => toast.error(intl.formatMessage({ id: 'document.watchlist.error' })),
        }
    );

    const [taglistOpen, setTagslistOpen] = useState(false);
    const handleTagslistOpen = () => setTagslistOpen(true);
    const handleTagslistClose = () => setTagslistOpen(false);

    const deviceType = watch('radiographyDeviceType');

    useEffect(() => {
        if (!deviceType) return;
        if (deviceType.code === RadiographyDeviceCode.XRAY) {
            form.setValue('radiographySource', null);
        } else {
            form.setValue('radiographyXraySource', null);
        }
    }, [deviceType, form.setValue]);

    return (
        <FormProvider {...form}>
            <DocumentPageStructure
                isLoading={isLoading}
                Header={
                    <>
                        <ActivityContentHeader
                            data={
                                // A bit verbose, but type-safe
                                pageData.variant === ActivityPageVariant.READ
                                    ? {
                                          variant: pageData.variant,
                                          changeRequest: isTaskChangeRequest,
                                          activityData: pageData.activityData,
                                      }
                                    : pageData.variant === ActivityPageVariant.CREATE
                                    ? {
                                          variant: ActivityPageVariant.CREATE,
                                      }
                                    : pageData.variant === ActivityPageVariant.CREATE_TEMPLATE
                                    ? {
                                          variant: ActivityPageVariant.CREATE_TEMPLATE,
                                      }
                                    : pageData.variant === ActivityPageVariant.READ_CHANGE_REQUEST
                                    ? {
                                          variant: pageData.variant,
                                          parentActivityData: pageData.parentActivityData,
                                          activityData: pageData.activityData,
                                      }
                                    : {
                                          variant: pageData.variant,
                                          activityData: getReadActivityData(pageData).activityData,
                                      }
                            }
                            mask={mask}
                            tags={activityTags}
                            showTags={pageData.variant === ActivityPageVariant.READ}
                            editable={isSavable || isCompletable}
                            isLoading={isLoading}
                            onRefreshClick={
                                variant === ActivityPageVariant.READ ||
                                variant === ActivityPageVariant.READ_CHANGE_REQUEST
                                    ? refreshDataWithUnsavedChangesPrompt
                                    : undefined
                            }
                            Main={
                                <>
                                    {pageData.variant === ActivityPageVariant.READ &&
                                        pageData.activityData.source === 'OIM' && (
                                            <Alert severity="info">
                                                <FormattedMessage
                                                    id="document.workflow.oldImpact.info"
                                                    values={{
                                                        action: (
                                                            // Copied from WorkflowHistory
                                                            <span
                                                                onClick={() =>
                                                                    window.open(
                                                                        getOIMUrl(
                                                                            `editActivity:${pageData.activityData.id}`,
                                                                            {},
                                                                            {},
                                                                            window.origin,
                                                                            environment
                                                                        )
                                                                    )
                                                                }
                                                                style={{
                                                                    textDecoration: 'underline',
                                                                    color: '#1976d2',
                                                                    cursor: 'pointer',
                                                                }}
                                                            >
                                                                <FormattedMessage id="document.workflow.oldImpact.activity" />
                                                            </span>
                                                        ),
                                                    }}
                                                />
                                            </Alert>
                                        )}
                                    {variant === ActivityPageVariant.READ && isTaskChangeRequest && (
                                        <Alert severity="warning">
                                            <FormattedMessage id="activity.changeRequest.warningCreate" />
                                        </Alert>
                                    )}
                                    {variant === ActivityPageVariant.READ_CHANGE_REQUEST && (
                                        <Alert severity="warning">
                                            {intl.formatMessage(
                                                { id: 'activity.changeRequest.warningRead' },
                                                {
                                                    activity: (
                                                        <Tooltip
                                                            title={intl.formatMessage({
                                                                id: 'activity.changeRequest.warningRead.tooltip',
                                                            })}
                                                        >
                                                            <Link
                                                                to={getRoute({
                                                                    path: ROUTES.activity.view,
                                                                    params: { id: pageData.parentActivityData.id },
                                                                })}
                                                            >
                                                                {pageData.parentActivityData.id}
                                                            </Link>
                                                        </Tooltip>
                                                    ),
                                                }
                                            )}
                                        </Alert>
                                    )}
                                </>
                            }
                            Left={
                                <Stack direction="row" spacing={1} alignItems="center">
                                    {tasks.currentTask !== null && (
                                        <TaskActions
                                            task={tasks.currentTask}
                                            onValidate={tasks.validate}
                                            validationData={tasks.validationData}
                                            onSubmit={tasks.perform}
                                            disabled={isLoading}
                                            getValues={form.getValues}
                                            isDirty={formIsDirty}
                                            context={context} //TODO
                                        />
                                    )}
                                    {isSavable && (
                                        <DocumentButtonsStack.Action
                                            {...([
                                                ActivityPageVariant.CREATE,
                                                ActivityPageVariant.CREATE_TEMPLATE,
                                                ActivityPageVariant.CLONE,
                                                ActivityPageVariant.CLONE_FROM_TEMPLATE,
                                            ].includes(variant)
                                                ? COMMON_DOCUMENT_BUTTONS.CREATE
                                                : COMMON_DOCUMENT_BUTTONS.SAVE)}
                                            onClick={withDispatchTaskEvent(() => {
                                                clearErrors();
                                                return handleSubmit((data) => saveActivity({ data }))();
                                            })}
                                            disabled={!isDirty}
                                            disablingReason={intl.formatMessage({
                                                id: 'document.form.disabledNoChange',
                                            })}
                                            important={!tasks.currentTask}
                                        />
                                    )}
                                </Stack>
                            }
                            Right={
                                <Stack direction="row" spacing={1} alignItems="center">
                                    {variant === ActivityPageVariant.READ && !!features[FeatureFlag.V9] && (
                                        <Box>
                                            <ResponsiveButton
                                                icon={<Label />}
                                                buttonVariant="outlined"
                                                onClick={handleTagslistOpen}
                                                disabled={!activityTags}
                                            >
                                                <FormattedMessage
                                                    id="document.action.tags"
                                                    values={{
                                                        total: activityTags ? activityTags.length : 0,
                                                    }}
                                                />
                                            </ResponsiveButton>
                                            <TaglistDialog
                                                activityId={activityId}
                                                handleClose={handleTagslistClose}
                                                open={taglistOpen}
                                                refetchActivityTags={refetchActivityTags}
                                                tags={activityTags}
                                            />
                                        </Box>
                                    )}
                                    {variant === ActivityPageVariant.READ && !!features[FeatureFlag.V9] && (
                                        <>
                                            {isWatching ? (
                                                <DocumentButtonsStack.Action
                                                    {...COMMON_DOCUMENT_BUTTONS.REMOVE_WATCHLIST}
                                                    disabled={isAddingToWatchlist || isRemovingFromWatchlist}
                                                    onClick={removeFromWatchlist}
                                                />
                                            ) : (
                                                <DocumentButtonsStack.Action
                                                    {...COMMON_DOCUMENT_BUTTONS.ADD_WATCHLIST}
                                                    disabled={isAddingToWatchlist || isRemovingFromWatchlist}
                                                    onClick={addToWatchlist}
                                                />
                                            )}

                                            <Box>
                                                <ResponsiveButton
                                                    icon={<People />}
                                                    buttonVariant="outlined"
                                                    onClick={handleWatchlistOpen}
                                                    disabled={!watchersData || watchersData.length === 0}
                                                >
                                                    <FormattedMessage
                                                        id="document.action.watchers"
                                                        values={{
                                                            total: watchersData ? watchersData.length : 0,
                                                        }}
                                                    />
                                                </ResponsiveButton>

                                                <WatchlistDialog
                                                    activityId={activityId}
                                                    handleClose={handleWatchlistClose}
                                                    open={watchlistOpen}
                                                    watchersData={watchersData}
                                                    readOnly={
                                                        !pageData.activityData.availableActions.canForwardForInformation
                                                    }
                                                    refetchWatchersData={refetchWatchersData}
                                                />
                                            </Box>
                                        </>
                                    )}
                                    {variant === ActivityPageVariant.READ &&
                                        !isTaskChangeRequest &&
                                        !!features[FeatureFlag.V9] && (
                                            <DocumentButtonsStack.Action
                                                {...COMMON_DOCUMENT_BUTTONS.CLONE}
                                                onClick={() => {
                                                    window.open(
                                                        getRoute({
                                                            path: ROUTES.activity.clone,
                                                            params: { id: String(pageData.activityData.id) },
                                                        })
                                                    );
                                                }}
                                                disabled={formIsDirty}
                                                disablingReason={intl.formatMessage({
                                                    id: 'document.form.disabledUnsaved',
                                                })}
                                            />
                                        )}
                                    {variant === ActivityPageVariant.READ &&
                                        pageData?.activityData?.isTemplate &&
                                        !!features[FeatureFlag.V9] && (
                                            <DocumentButtonsStack.Action
                                                {...COMMON_DOCUMENT_BUTTONS.CLONE_FROM_TEMPLATE}
                                                onClick={() => {
                                                    window.open(
                                                        getRoute({
                                                            path: ROUTES.activity.cloneFromTemplate,
                                                            params: { id: String(pageData.activityData.id) },
                                                        })
                                                    );
                                                }}
                                                disabled={
                                                    formIsDirty ||
                                                    !pageData?.activityData?.activityParticipants?.some(
                                                        (participant) => participant.person?.id === currentUser.id
                                                    ) ||
                                                    pageData?.activityData?.status?.code !== ActivityStatusCode.APPROVED
                                                }
                                                disablingReason={intl.formatMessage({
                                                    id: 'document.form.disabledTemplateNotApproved',
                                                })}
                                            />
                                        )}
                                    {variant === ActivityPageVariant.READ &&
                                        pageData.activityData.type?.code === ActivityTypeCode.GUIDED_VISIT && (
                                            <PrintGuidedVisitsButton
                                                formIsDirty={formIsDirty}
                                                activity={pageData.activityData}
                                            />
                                        )}
                                    {variant === ActivityPageVariant.READ && (
                                        <AttachmentsAction
                                            attachments={pageData.activityData.attachments}
                                            linkAttachments={(attachmentIds) =>
                                                activityAttachmentsApi.linkAttachments({
                                                    id: pageData.activityData.id,
                                                    taskId: tasks.currentTask?.id!,
                                                    ...attachmentIds,
                                                })
                                            }
                                            unlinkAttachments={(attachmentIds) =>
                                                activityAttachmentsApi.unlinkAttachments({
                                                    id: pageData.activityData.id,
                                                    taskId: tasks.currentTask?.id!,
                                                    ...attachmentIds,
                                                })
                                            }
                                            updateAttachment={(data) =>
                                                activityAttachmentsApi.updateAttachment({
                                                    id: pageData.activityData.id,
                                                    taskId: tasks.currentTask?.id!,
                                                    ...data,
                                                })
                                            }
                                            // TODO once the permissions are implemented
                                            readOnly={pageData.activityData.source === 'OIM'}
                                        />
                                    )}
                                    {/* TODO */}
                                    {/*variant === ActivityPageVariant.READ && !isTaskChangeRequest && (
                                    <>
                                        <FavouriteButton activityId={pageData.activityData.id} />
                                        <ButtonSet buttons={informationButtons} color={'grey'} />
                                    </>
                                )*/}
                                </Stack>
                            }
                        />
                    </>
                }
                Body={
                    <>
                        {(variant === ActivityPageVariant.READ ||
                            variant === ActivityPageVariant.READ_CHANGE_REQUEST) && (
                            <LinkedDocumentsPanel
                                type={EntityType.ActivityNim}
                                activity={pageData.activityData}
                                collapsed
                            />
                        )}
                        <ValidationErrorsPanel schema={uiSchemaActivity} customResolver={customErrorResolver} />
                        <FieldsAttributesContextProvider value={attributes}>
                            <FormGenerator rootElement={uiSchemaActivity} form={form} disabled={isLoading} />
                            {(variant === ActivityPageVariant.READ ||
                                variant === ActivityPageVariant.READ_CHANGE_REQUEST) && (
                                <>
                                    <WorkflowHistory
                                        // Works for both Activity and change request
                                        documentType={DocumentType.ACTIVITY}
                                        documentId={pageData.activityData.id}
                                    />
                                    <CommentList
                                        documentDetails={[
                                            {
                                                documentType: DocumentType.ACTIVITY,
                                                documentId: pageData.activityData.id,
                                            },
                                        ]}
                                        currentTask={tasks.currentTask}
                                        addCommentCallback={addActivityComment}
                                        disabled={isLoading || pageData.activityData.source === 'OIM'}
                                        isEditableWithoutTasks={true}
                                    />
                                </>
                            )}
                        </FieldsAttributesContextProvider>
                    </>
                }
                Drawer={
                    !isOIMActivity &&
                    (variant === ActivityPageVariant.READ || variant === ActivityPageVariant.READ_CHANGE_REQUEST) ? (
                        <TaskHistory
                            tasks={tasks}
                            disabled={isLoading}
                            withUnsavedChangesPrompt={withUnsavedChangesPrompt}
                            onTaskChange={() => form.reset()}
                            onTaskUpdate={refreshData}
                        />
                    ) : null
                }
            />
        </FormProvider>
    );
};
